import { NodeV2 } from '@vedalib/editor'
import cn from 'classnames'
import * as R from 'ramda'
import React, { useMemo, useState } from 'react'

import DropDown from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { updateBlock } from 'services/Store/Project/actions'
import { useProjectDispatch } from 'services/Store/Project/hooks'
import { updateNodeChildren, updateNodeFullStyle } from 'services/Store/Project/mutators/node'
import { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import { safeParseGrid } from '../formControls/Grid/utils'
import * as s from './RootPicker.module.scss'
import { IRootPreset, PRESETS, PROP_KEYS } from './presets'

interface IRootPickerProps {
  type: 'grid' | 'flex' | 'absolute' | 'element'
  node: NodeV2
  block: Block
}

const RootPicker: React.FC<IRootPickerProps> = ({ block, type, node }) => {
  const dispatch = useProjectDispatch()
  const [visible, setVisible] = useState(false)
  const current = useMemo(() => {
    return PRESETS.find(({ style, children }) => {
      const preset = R.mapObjIndexed((s) => {
        const { min, items } = safeParseGrid(s.gridColumns)
        return {
          ...R.pick(PROP_KEYS, s),
          gridColumns: { min, items: items.map((item) => item.data) },
        }
      }, style)
      const nodeStyle = R.mapObjIndexed((s) => {
        const { min, items } = safeParseGrid(s.gridColumns)
        return {
          ...R.pick(PROP_KEYS, s),
          gridColumns: { min, items: items.map((item) => item.data) },
        }
      }, node.style)

      const isEqual = R.equals(preset, nodeStyle)
      return isEqual && (children ? children.length === node.children.length : true)
    })
  }, [node.style, node.children.length])

  const handleClickPreset = ({ style, children }: IRootPreset) => {
    const value = R.compose(
      children?.length ? updateNodeChildren(node.id, children) : R.identity,
      updateNodeFullStyle(node.id, style),
    )(block)
    dispatch(updateBlock({ id: block.uuid, value }))
    setVisible(false)
  }

  return (
    <DropDown
      onVisibleChange={setVisible}
      overlay={
        <div className={s.overlay}>
          <Typography styleType='hint'>{t('editorV2.presets.root.help')}</Typography>
          <div className={s.presets}>
            {PRESETS.map((preset) => (
              <div
                className={cn(s.item, { [s.selected]: current?.id === preset.id })}
                key={preset.id}
                onClick={() => handleClickPreset(preset)}
              >
                <img className={s.image} src={preset.image} />
                {preset.title}
              </div>
            ))}
          </div>
        </div>
      }
      placement='bottom-start'
      styleType='clear'
      visible={visible}
    >
      {({ open }) => (
        <div className={cn(s.root, s.active, s[type])}>
          <Typography color='inherit' size={KitSize.XS}>
            {current?.title || t('editorV2.presets.root.custom')}
          </Typography>
          <Icon name={open ? 'otherUp' : 'otherDown'} size={KitSize.XS} />
        </div>
      )}
    </DropDown>
  )
}

export default React.memo(RootPicker)
